/**
 * Classe canônica de erro vinda da API DelivAI v2 (backend Kotlin).
 *
 * O ErrorAdvice do backend padroniza qualquer falha como:
 *   { "ok": false, "status": 500, "error": "internal_error",
 *     "message": "...", "details": {...}, "correlation_id": "..." }
 * — esta classe encapsula isso para o front consumir sem se preocupar com
 * estruturas diferentes ou erros de fetch puro. Também aceita o shape legado
 * `{ error: { code, message, details } }` por compatibilidade.
 */
export type ApiErrorPayload = {
  code: string;
  message: string;
  details?: Record<string, unknown>;
  correlationId?: string;
};

export class ApiClientError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: Record<string, unknown>;
  readonly correlationId?: string;
  /** O client já tentou de novo. O React Query não repete em cima. */
  retriesExhausted = false;

  constructor(args: {
    code: string;
    message: string;
    status: number;
    details?: Record<string, unknown>;
    correlationId?: string;
  }) {
    super(args.message);
    this.name = "ApiClientError";
    this.code = args.code;
    this.status = args.status;
    this.details = args.details;
    this.correlationId = args.correlationId;
  }

  isUnauthorized(): boolean {
    return this.status === 401 || this.code === "UNAUTHORIZED";
  }

  isForbidden(): boolean {
    return this.status === 403 || this.code === "FORBIDDEN";
  }

  isNotFound(): boolean {
    return this.status === 404 || this.code.endsWith("_NOT_FOUND");
  }

  isValidation(): boolean {
    return this.status === 400 || this.code === "VALIDATION_ERROR";
  }

  isServer(): boolean {
    return this.status >= 500;
  }

  isAborted(): boolean {
    return this.code === "ABORTED";
  }

  isRateLimited(): boolean {
    return this.status === 429 || this.code === "RATE_LIMITED";
  }
}

export function isRequestAborted(err: unknown): boolean {
  if (err instanceof ApiClientError) return err.isAborted();
  return (err as { name?: string })?.name === "AbortError";
}

export function isRateLimited(err: unknown): boolean {
  return err instanceof ApiClientError && err.isRateLimited();
}

/** Uma nova tentativa após 429; abortos sobem na hora. */
export async function retryOnceOnRateLimit<T>(
  fn: () => Promise<T>,
  waitMs = 800
): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (isRequestAborted(err) || !isRateLimited(err)) throw err;
    await new Promise((r) => setTimeout(r, waitMs));
    return fn();
  }
}

/** Heurística para extrair o payload de erro padronizado do backend Kotlin. */
export function parseApiErrorPayload(
  raw: unknown,
  fallbackStatus: number
): ApiErrorPayload {
  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    const correlationId =
      typeof obj.correlation_id === "string"
        ? obj.correlation_id
        : typeof obj.correlationId === "string"
          ? obj.correlationId
          : undefined;

    // Shape novo do ErrorAdvice: { ok, status, error: "code", message, details, correlation_id }
    if (typeof obj.error === "string") {
      return {
        code: obj.error,
        message:
          typeof obj.message === "string" ? obj.message : `Erro ${fallbackStatus}`,
        details:
          obj.details && typeof obj.details === "object"
            ? (obj.details as Record<string, unknown>)
            : undefined,
        correlationId,
      };
    }

    // Shape legado: { error: { code, message, details } }
    if (obj.error && typeof obj.error === "object") {
      const inner = obj.error as Record<string, unknown>;
      return {
        code: typeof inner.code === "string" ? inner.code : `HTTP_${fallbackStatus}`,
        message:
          typeof inner.message === "string"
            ? inner.message
            : `Erro ${fallbackStatus}`,
        details:
          inner.details && typeof inner.details === "object"
            ? (inner.details as Record<string, unknown>)
            : undefined,
        correlationId,
      };
    }

    // Resposta apenas com `message` (raro).
    if (typeof obj.message === "string") {
      return {
        code: `HTTP_${fallbackStatus}`,
        message: obj.message,
        correlationId,
      };
    }
  }

  if (fallbackStatus === 404) {
    return {
      code: "ENDPOINT_NOT_FOUND",
      message:
        "Endpoint não encontrado no servidor. Verifique se o backend está atualizado com a última versão.",
    };
  }
  return {
    code: `HTTP_${fallbackStatus}`,
    message: `Erro ${fallbackStatus}`,
  };
}

/** Mensagem legível para toast/UI a partir do payload do ErrorAdvice. */
export function formatApiErrorForUser(parsed: ApiErrorPayload): string {
  const parts: string[] = [];
  const msg = parsed.message?.trim();
  const cause =
    parsed.details && typeof parsed.details.cause === "string"
      ? parsed.details.cause.trim()
      : "";
  const errClass =
    parsed.details && typeof parsed.details.error_class === "string"
      ? parsed.details.error_class.trim()
      : "";

  if (msg && msg !== "erro interno") parts.push(msg);
  else if (cause) parts.push(cause);
  else if (parsed.code && parsed.code !== "internal_error") parts.push(parsed.code);

  if (cause && !parts.includes(cause)) parts.push(cause);
  if (errClass && parts.length === 0) parts.push(errClass);

  if (parsed.correlationId) {
    parts.push(`(ref: ${parsed.correlationId.slice(0, 8)}…)`);
  }

  if (parts.length === 0) {
    return "Erro no servidor. Abra Painel DelivAI → Logs, filtre Source «orders» e status Erro.";
  }
  return parts.join(" — ");
}
